/**
 * KANBAN BOARD — board generica e AGNOSTICA rispetto all'entità della card.
 *
 * Conosce solo: colonne (gli stati), item (ciascuno con la colonna in cui sta) e
 * `renderCard` (come si disegna la card di QUELLA entità). Non sa cosa sia un
 * preventivo o un task: la pagina che la monta lo adatta.
 *
 * Due modalità:
 *  - INTERATTIVA (default): DnD via `useDraggable`/`useDroppable` + `DragOverlay`;
 *    trascinare una card in un'altra colonna la sposta (`onMove`). È il caso dei
 *    preventivi (id numerici = pipelineStateId).
 *  - READ-ONLY (`readonly`): sola visualizzazione, card NON trascinabili, niente
 *    DnD. È il caso dei task (stati derivati dalla data, non settabili).
 *
 * Gli id di colonna/item sono `string | number` (i preventivi usano numeri, i task
 * usano gli stati-stringa). `variant` applica uno o più skin alla root (`kanban-board--x`).
 * Zero stile inline: lo stato di trascinamento è la classe `.is-dragging`. Unica eccezione la
 * variabile `--kanban-board-height`, che solo il codice può calcolare (vedi `useFillViewport`).
 */

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  pointerWithin,
  useDraggable,
  useDroppable,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useDndSensors } from './dnd';

type Id = string | number;

/** Una colonna della board = uno stato. */
export interface KanbanColumn {
  id: Id;
  title: string;
  /** Accento visivo (stilizzato via `data-tone`): initial/won/lost/default per i
   *  preventivi, crit/warn/info/todo/ok per i task. Resta `string` e non un'unione
   *  proprio perché ogni entità porta il proprio vocabolario di toni. */
  tone?: string;
  /**
   * Quante card ha la colonna in TUTTO, non quante ne ha in mano la board. Serve a chi carica le
   * colonne a pagine: senza, l'intestazione direbbe «20» di una colonna che ne ha 300. Assente, il
   * conteggio è quello delle card ricevute, come per chi le passa tutte.
   */
  count?: number;
}

/** Un elemento della board. `data` è l'entità concreta, opaca per la board. */
export interface KanbanBoardItem<T> {
  id: Id;
  columnId: Id;
  data: T;
}

interface KanbanBoardProps<T> {
  columns: KanbanColumn[];
  items: KanbanBoardItem<T>[];
  /** Disegna il contenuto della card per l'entità concreta. */
  renderCard: (data: T, ctx: { dragging: boolean }) => ReactNode;
  /** Persisti lo spostamento (solo modalità interattiva). */
  onMove?: (itemId: Id, targetColumnId: Id) => void;
  /** Testo mostrato in una colonna senza card. */
  emptyLabel?: string;
  /** Sola visualizzazione: niente DnD, card non trascinabili. */
  readonly?: boolean;
  /**
   * Skin della root: `kanban-board--<variant>`, uno o più.
   *
   * Più d'uno perché gli skin si sommano e appartengono a posti diversi: `toned` (colonne
   * colorate) è del design system e lo disegna il core, mentre uno skin di dominio — la
   * bacheca delle Pratiche di Pollini — lo disegna il plugin nel proprio foglio di stile.
   * Con un solo modificatore il core avrebbe dovuto conoscere per nome le entità dei
   * clienti, che è esattamente ciò che questa board non fa.
   */
  variant?: string | string[];
  /**
   * Il fondo di una colonna, sotto le card: per esempio «Mostra altre» di chi carica le colonne a
   * pagine. La board non sa cosa ci sia sotto, lo disegna chi la monta.
   */
  renderColumnFooter?: (column: KanbanColumn) => ReactNode;
}

/** Sotto questa altezza la board non scende: su una finestra minuscola scorre la pagina. */
const MIN_BOARD_HEIGHT = 240;

/**
 * LA BOARD FINISCE AL FONDO DELLO SCHERMO, e la pagina non scorre.
 *
 * Alta quanto la colonna più lunga, la board spingeva la scrollbar orizzontale fuori dalla
 * finestra: per passare da uno stato all'altro bisognava scendere in fondo alla pagina, perdendo
 * di vista le card in testa. Ora è alta quanto lo spazio che resta sotto di lei, e oltre scorrono
 * le card dentro la colonna.
 *
 * Lo spazio si MISURA perché il CSS non lo sa: sopra la board ogni pagina ha una testata diversa
 * (tab dei task, titolo e filtro dei preventivi, ricerca delle pratiche). Con un'altezza fissa
 * restava uno scorrimento di pagina grande quanto la testata, in più delle scrollbar delle colonne.
 *
 * Si rimisura quando cambia la finestra e quando cambia il genitore — la testata che va a capo su
 * uno schermo stretto, un avviso che compare sopra. Il conto passa per `requestAnimationFrame`: il
 * genitore cambia anche per effetto della misura stessa, e senza il rinvio il `ResizeObserver`
 * segnalerebbe un ciclo.
 *
 * `readonly` è nelle dipendenze perché le due modalità rendono due `div` diversi: cambiando, la
 * misura deve seguire quello nuovo.
 */
function useFillViewport(ref: RefObject<HTMLDivElement | null>, readonly: boolean) {
  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) return;

    let frame = 0;

    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Il margine in fondo al contenitore della pagina si toglie anche lui: è spazio che la
      // pagina occupa sotto la board, e farebbe ricomparire la scrollbar.
      const main = el.closest('.main');
      const bottom = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const height = Math.max(MIN_BOARD_HEIGHT, Math.floor(window.innerHeight - top - bottom));

      el.style.setProperty('--kanban-board-height', `${height}px`);
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    measure();

    const observer = new ResizeObserver(schedule);

    if (el.parentElement) observer.observe(el.parentElement);

    window.addEventListener('resize', schedule);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [ref, readonly]);
}

// Prefissi per separare gli spazi degli id draggable (card) e droppable (colonna).
const CARD = 'card:';
const COL = 'col:';

export function KanbanBoard<T>({
  columns,
  items,
  renderCard,
  onMove,
  emptyLabel,
  readonly = false,
  variant,
  renderColumnFooter,
}: KanbanBoardProps<T>) {
  const sensors = useDndSensors();
  const boardRef = useRef<HTMLDivElement>(null);

  useFillViewport(boardRef, readonly);

  /**
   * Il puntatore decide quando c'è, la geometria solo quando non c'è.
   *
   * Con `closestCorners` da solo la colonna vincente si sceglie sulla distanza media fra angoli
   * OMOLOGHI, e gli angoli bassi di una colonna piena stanno centinaia di pixel più in giù: una
   * colonna con quindici card arrivava a y=1643 su una finestra alta 918, e per qualunque rilascio
   * sopra y≈900 vinceva una colonna VICINA E CORTA, anche col puntatore dentro quella giusta. Se la
   * vincente era la colonna di partenza, `handleDragEnd` usciva in silenzio: la card tornava al suo
   * posto e non compariva nessun errore.
   *
   * Il sintomo peggiorava con l'uso — più card, colonna più alta, bersaglio più difficile — ed era
   * invisibile su una board quasi vuota.
   *
   * `closestCorners` resta come ripiego perché serve alla **tastiera**: `pointerWithin` non
   * restituisce niente quando un puntatore non c'è, e i sensori qui sono due (vedi `dnd.ts`).
   */
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const byPointer = pointerWithin(args);

    return byPointer.length > 0 ? byPointer : closestCorners(args);
  }, []);
  const [activeId, setActiveId] = useState<Id | null>(null);

  const activeItem = activeId != null ? items.find((it) => it.id === activeId) ?? null : null;

  const variants = variant == null ? [] : [variant].flat();
  const boardClass = ['kanban-board', ...variants.map((v) => `kanban-board--${v}`)].join(' ');

  function handleDragStart(event: DragStartEvent) {
    const raw = String(event.active.id);
    if (!raw.startsWith(CARD)) return;
    const idStr = raw.slice(CARD.length);
    const item = items.find((it) => String(it.id) === idStr);
    setActiveId(item ? item.id : null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeRaw = String(active.id);
    const overRaw = String(over.id);
    if (!activeRaw.startsWith(CARD) || !overRaw.startsWith(COL)) return;

    const item = items.find((it) => String(it.id) === activeRaw.slice(CARD.length));
    const targetCol = columns.find((c) => String(c.id) === overRaw.slice(COL.length));
    if (!item || !targetCol || String(item.columnId) === String(targetCol.id)) return;

    onMove?.(item.id, targetCol.id);
  }

  const columnsJsx = columns.map((col) => {
    // Confronto per stringa come nel resto del DnD: la board è generica (id string|number)
    // e un consumer potrebbe usare tipi misti tra columnId e column.id.
    const colItems = items.filter((it) => String(it.columnId) === String(col.id));
    const cards = colItems.map((it) =>
      readonly ? (
        // `--static`: niente mano che afferra sotto il puntatore. Il cursore è una
        // promessa, e su una board in sola lettura sarebbe una promessa falsa.
        <div key={it.id} className="kanban-card kanban-card--static">
          {renderCard(it.data, { dragging: false })}
        </div>
      ) : (
        <BoardCard key={it.id} id={it.id}>
          {(dragging) => renderCard(it.data, { dragging })}
        </BoardCard>
      ),
    );
    const body = (
      <>
        {cards}
        {colItems.length === 0 && <p className="kanban-column__empty">{emptyLabel}</p>}
        {renderColumnFooter?.(col)}
      </>
    );
    const count = col.count ?? colItems.length;
    return readonly ? (
      <StaticColumn key={col.id} column={col} count={count}>
        {body}
      </StaticColumn>
    ) : (
      <BoardColumn key={col.id} column={col} count={count}>
        {body}
      </BoardColumn>
    );
  });

  if (readonly) {
    return (
      <div ref={boardRef} className={boardClass}>
        {columnsJsx}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div ref={boardRef} className={boardClass}>
        {columnsJsx}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="kanban-card kanban-card--overlay">
            {renderCard(activeItem.data, { dragging: true })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function BoardColumn({
  column,
  count,
  children,
}: {
  column: KanbanColumn;
  count: number;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${COL}${column.id}` });

  return (
    <section
      ref={setNodeRef}
      className={`kanban-column${isOver ? ' is-over' : ''}`}
      data-tone={column.tone ?? 'default'}
    >
      <header className="kanban-column__header">
        <span>{column.title}</span>
        <span className="kanban-column__count">{count}</span>
      </header>
      <div className="kanban-column__cards">{children}</div>
    </section>
  );
}

function BoardCard({
  id,
  children,
}: {
  id: Id;
  children: (dragging: boolean) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${CARD}${id}` });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-card${isDragging ? ' is-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      {children(isDragging)}
    </div>
  );
}

function StaticColumn({
  column,
  count,
  children,
}: {
  column: KanbanColumn;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="kanban-column" data-tone={column.tone ?? 'default'}>
      <header className="kanban-column__header">
        <span>{column.title}</span>
        <span className="kanban-column__count">{count}</span>
      </header>
      <div className="kanban-column__cards">{children}</div>
    </section>
  );
}
